import { useEffect, useState } from "react";
import { api, type SettingsResponse } from "../api";
import { useToast } from "../components/Toast";

export default function Settings() {
  const [settings, setSettings] = useState<SettingsResponse | null>(null);
  const [models, setModels] = useState<{ id: string; name: string }[]>([]);
  const [model, setModel] = useState("");
  const [aiEnabled, setAiEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  useEffect(() => {
    api
      .getSettings()
      .then((s) => {
        setSettings(s);
        setModel(s.aiModel ?? "");
      })
      .catch((e) => toast.show("error", (e as Error).message));
    api.aiModels().then((r) => setModels(r.models)).catch(() => setModels([]));
    api
      .aiStatus()
      .then((s) => setAiEnabled(s.enabled))
      .catch(() => setAiEnabled(false));
  }, []);

  async function save(value: string | null) {
    setBusy(true);
    try {
      const s = await api.putSettings(value);
      setSettings(s);
      setModel(s.aiModel ?? "");
      toast.show("success", "Saved");
    } catch (e) {
      toast.show("error", (e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function test() {
    setBusy(true);
    try {
      const r = await api.aiTest(model.trim() || undefined);
      if (r.ok) toast.show("success", "OK");
      else toast.show("error", r.error ?? "Test failed");
    } catch (e) {
      toast.show("error", (e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!settings) return <div className="text-gh-muted">Loading…</div>;

  return (
    <div className="max-w-2xl">
      <h2 className="text-lg font-semibold mb-4">AI Translation</h2>

      {!aiEnabled && (
        <div className="mb-4 px-3 py-2 rounded border border-yellow-500/40 bg-yellow-500/10 text-sm">
          OpenRouter API key not configured (set <code>OPENROUTER_API_KEY</code>).
        </div>
      )}

      <label className="block text-sm mb-1">Model</label>
      <input
        type="text"
        list="ai-model-options"
        data-testid="ai-model-input"
        value={model}
        disabled={!aiEnabled || busy}
        onChange={(e) => setModel(e.target.value)}
        placeholder={settings.default}
        className="w-full px-2 py-1 rounded border border-gh-border text-sm disabled:opacity-50"
      />
      <datalist id="ai-model-options">
        {models.map((m) => (
          <option key={m.id} value={m.id}>
            {m.name}
          </option>
        ))}
      </datalist>

      <p className="text-xs text-gh-muted mt-2">
        Effective: <span className="font-mono">{settings.effectiveModel}</span> ·
        from env: <span className="font-mono">{settings.envModel ?? "—"}</span> ·
        default: <span className="font-mono">{settings.default}</span>
      </p>

      <div className="flex gap-2 mt-3">
        <button
          type="button"
          data-testid="ai-model-save"
          disabled={!aiEnabled || busy}
          onClick={() => save(model.trim() || null)}
          className="px-3 py-1 rounded border border-gh-border text-sm disabled:opacity-50"
        >
          Save
        </button>
        <button
          type="button"
          data-testid="ai-model-test"
          disabled={!aiEnabled || busy}
          onClick={test}
          className="px-3 py-1 rounded border border-gh-border text-sm disabled:opacity-50"
        >
          Test
        </button>
        <button
          type="button"
          data-testid="ai-model-reset"
          disabled={!aiEnabled || busy}
          onClick={() => save(null)}
          className="px-3 py-1 rounded border border-gh-border text-sm disabled:opacity-50"
        >
          Reset to env default
        </button>
      </div>
    </div>
  );
}
